import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
  /** Width as Tailwind class (e.g. "w-32", "w-full") */
  width?: string;
  /** Height as Tailwind class (e.g. "h-4", "h-10") */
  height?: string;
  /** Shape variant */
  variant?: "text" | "circle" | "rect" | "banner";
}

const variantClasses = {
  text: "rounded-md",
  circle: "rounded-full aspect-square",
  rect: "rounded-lg",
  banner: "rounded-xl h-44 w-full",
};

/**
 * Shimmer-animated loading placeholder.
 * Uses the same shimmer keyframe as PageBanner for visual consistency.
 */
export function Skeleton({
  className,
  width = "w-full",
  height = "h-4",
  variant = "text",
}: SkeletonProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden bg-[var(--color-bg-tertiary)]",
        variantClasses[variant],
        variant !== "banner" && variant !== "circle" && width,
        variant !== "banner" && height,
        className
      )}
      aria-hidden="true"
    >
      <div className="skeleton-shimmer absolute inset-0" />
    </div>
  );
}

/**
 * Pre-composed skeleton for table rows.
 * Renders n rows of shimmer placeholders matching a table layout.
 */
export function SkeletonTable({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="rounded-xl border border-[var(--color-border-light)] overflow-hidden">
      {/* Header */}
      <div className="flex gap-4 bg-[var(--color-bg-secondary)] px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} width="flex-1" height="h-3" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className="flex gap-4 px-4 py-3 border-t border-[var(--color-border-light)]"
          style={{ animationDelay: `${rowIdx * 60}ms` }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton
              key={colIdx}
              width="flex-1"
              height="h-3"
              className={cn(colIdx === 0 && "max-w-[60%]")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Full page skeleton matching the standard page layout
 * (banner + content area).
 */
export function SkeletonPage() {
  return (
    <div className="animate-fade-in">
      <Skeleton variant="banner" />
      <div className="space-y-4 mt-6">
        <Skeleton width="w-48" height="h-5" />
        <Skeleton width="w-72" height="h-9" />
        <SkeletonTable />
      </div>
    </div>
  );
}
